import { z } from 'zod'

// Replaces the old, wrong `addInvestorSchema` (`features/cases/schemas/case-forms.schema.ts`) —
// its mobile regex was `/^\d{10}$/` (accepted a leading 0-5) vs. the backend's `/^[6-9]\d{9}$/`,
// and `pan` was wrongly required. Mirrors `CreateInvestorDto` exactly.
export const createInvestorSchema = z.object({
  name: z.string().min(2, 'Enter the investor’s full name.'),
  email: z.string().email('Enter a valid email address.'),
  mobile: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number.'),
  pan: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z
      .string()
      .regex(/^[A-Za-z]{5}\d{4}[A-Za-z]$/, 'Enter a valid PAN, e.g. ABCPS1234D.')
      .transform((v) => v.toUpperCase())
      .optional(),
  ),
  city: z.string().optional(),
})
export type CreateInvestorFormValues = z.infer<typeof createInvestorSchema>

// New — mirrors `UpdateInvestorDto`, all optional. Backs the staff-only "Edit investor details"
// surface in client-detail.tsx, used once an investor already has a login account (before that,
// the Pencil still opens `SaveInvestorProfileDialog`, which requires PAN).
export const updateInvestorSchema = z.object({
  name: z.string().min(2, 'Enter the investor’s full name.').optional(),
  email: z.string().email('Enter a valid email address.').optional(),
  mobile: z
    .string()
    .regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number.')
    .optional(),
  pan: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z
      .string()
      .regex(/^[A-Za-z]{5}\d{4}[A-Za-z]$/, 'Enter a valid PAN, e.g. ABCPS1234D.')
      .transform((v) => v.toUpperCase())
      .optional(),
  ),
  city: z.string().optional(),
  // Blank-skips-validation, same convention as `pan` above — a general investor attribute (not
  // Build-Portfolio-exclusive), but this dialog is the only UI path to set it today.
  age: z.preprocess(
    (v) => (v === '' || v === undefined ? undefined : v),
    z.coerce.number().int().min(1, 'Enter a valid age.').max(120, 'Enter a valid age.').optional(),
  ),
})
export type UpdateInvestorFormValues = z.infer<typeof updateInvestorSchema>

// Replaces `clientProfileSchema` — shrunk to match `UpdateInvestorMeDto` exactly. That DTO has no
// email/mobile fields at all, and the global ValidationPipe's `forbidNonWhitelisted: true` means
// sending either is a 422, not a silently-dropped field — so the fields must not exist on this form.
export const updateMyProfileSchema = z.object({
  name: z.string().min(2, 'Enter your full name.'),
  city: z.string().optional(),
})
export type UpdateMyProfileFormValues = z.infer<typeof updateMyProfileSchema>
